import { useEffect, useMemo, useState } from 'react';
import { PrivateStackParamList } from '../../navigationApp/types';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import {  ThemedView } from '@app/theme';
import KeyboardDismissView from '@app/hoc/KeyboardDismissView';
import { CreditApplicationStepId, getParameters, removeUrlWithoutReload, trackEvent } from '@app/utils';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { RootState } from '@app/store/newstore';
import { editableErroCode, getApplicationId, getFetchSDKData, getRtaDetails, handleFetchPortfolioInit } from './utils';
import { DefaultRTA } from '@app/types';
import {
    ACTION_TYPE,
    AssetRepository,
    FetchPortfolioError,
    FetchPortfolioInitResponse,
    FetchPortfolioModalType
} from './type';
import EditMobileNumberModal from './components/EditMobileNumberModal';
import EditRtaModal from './components/EditRtaModal';
import Header from './components/Header';
import Body from './components/Body';
import Footer from './components/Footer';
import VerifyOtpModal from './components/VerifyOtpModal';
import FetchPortfolioModals from './components/FetchPortfolioModals';
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet';
import ContactUs from '../UserProfile/ContactUs';
import { updateUserDetailBorrowerAccountName, updateUserDetailPan, updateUserDetails } from '@app/store/slices/authSlice';
import { setAuthCAS } from '@app/store/slices/applicationSlice';
import PageLoader from '@sharedComponents/PageLoader/PageLoader';
import handleApiErr from '@app/helper/handleApiErr';
import { showErrorSheet } from '@app/store/slices/errorSlice';
import { getNextRoute } from '@app/navigationApp/navigationUtil';
import ROUTE from '@app/navigationApp/routes';
import { Platform } from 'react-native';
import EditPanFlow from './components/EditPanFlow';
import UseIsKeyboardOpen from '@app/hooks/useIsKeyBoardOpen';
import { endpoints } from '@app/api/apis';
import axios from 'axios';
import MFCFetchModal from '@app/sharedComponents/MFCFetchModal';

type FetchPortfolioProps = NativeStackScreenProps<PrivateStackParamList, 'MF_FETCH_PORTFOLIO'> & {
    retryingFetchPortfolio?: boolean;
    rtaToRetry?: string;
    errorData?: any;
    onSuccessFetch?: () => void;
    isFromMFPortfolio?: boolean;
}


const entitiesMap: Record<string, string> = {
    'MFCentral': 'MFCentral',
    'KFINTECH': 'KFintech',
    'CAMS': 'CAMS'
}


const buildTitle = (data: DefaultRTA[]) => {
    const entities = data?.map((item: DefaultRTA) => item.repositoryName);
    const entitiesList = entities.length === 3
        ? `${entitiesMap[entities[0]]}, ${entitiesMap[entities[1]]} & ${entitiesMap[entities[2]]}`
        : entities.join(' & ');
    return `${entitiesList} ${entities.length > 1 ? 'are' : 'is'} SEBI regulated entit${entities.length > 1 ? 'ies' : 'y'} which maintain records of mutual investments.`;
}

const FetchPortfolio = ({ navigation, route, retryingFetchPortfolio = false, rtaToRetry = '', onSuccessFetch , isFromMFPortfolio=false , isFullScreen}: FetchPortfolioProps) => {
    const { isEnhancementApplicationInProgress = false } = route?.params ?? {};
    const dispatch = useAppDispatch();
    const [buttonLoader, setButtonLoader] = useState(false);
    const {isKeyboardOpen} = UseIsKeyboardOpen();
    const { userDetails } = useAppSelector((state: RootState) => state.auth);
    const [panNumber, setPanNumber] = useState(null);
    const [mobileNumber, setMobileNumber] = useState<string>('');
    const [openUpdateMobileNumber, setOpenUpdateMobileNumber] = useState(false);
    const [defaultRTAData, setDefaultRTAData] = useState<DefaultRTA[] | null>(null);
    const [rtaDetailsFetching, setRtaDetailsFetching] = useState<boolean>(false);
    const [openRTAEdit, setOpenRTAEdit] = useState<boolean>(false);
    const [portfolioInitData, setPortfolioInitData] = useState<FetchPortfolioInitResponse | null>(null);
    const [errorModalData, setErrorModalData] = useState<FetchPortfolioError | null>(null);
    const [showNeedHelp, setShowNeedHelp] = useState<boolean>(false);
    const [hasFetchErrorOccured, setHasFetchErrorOccured] = useState<boolean>(false);
    const [pageLoader, setPageLoader] = useState(false);
    const [title, setTitle] = useState<string>('');
    const [showLoadingLottie, setShowLoadingLottie] = useState(false);
    const [showSuccessLottie, setShowSuccessLottie] = useState(false);
    const [buttonFetching, setButtonFetching] = useState(false);
    const [showInfo,setShowInfo] = useState(isFromMFPortfolio ? false : true);
    const [retryErrorCount,setRetryErrorCount] = useState(0)
    const [showEditablePanFlow, setShowEditablePanFlow] = useState<boolean>(false);
    const [panEditable, setPanEditable] = useState<boolean>(true);
    const [mfcFetchURL ,setMfcFetchURL] = useState('')

    const [fetchFlowType , setFetchFlowType] = useState<'VOLT_RTA' | 'FENIX_SDK' | null>(null)

    const fetchUserProfile = () => {
        const url = endpoints.userProfile + userDetails?.linkedApplications[0]?.accountId
        axios.get(url).then((res: any) => {
            dispatch(updateUserDetailBorrowerAccountName(res?.data?.name))
        }).catch((err) => {
            console.log('fetchUserProfile', err)
        })
    }

    useEffect(() => {
        if(route?.params?.errorData) {
            setErrorModalData(route?.params?.errorData);
        }
    }, [route?.params?.errorData])


    const getFetchSDKDataFromNetwork = async () => {
        const applicationId = getApplicationId(userDetails?.linkedApplications[0]?.applicationId);
        await getFetchSDKData(
            applicationId,
            panNumber!!,
            mobileNumber,
            (data: any) => {
                if (data?.stepResponseObject?.error) {
                    setButtonLoader(false);
                    setErrorModalData(data?.stepResponseObject?.error);
                } else if (data?.stepResponseObject?.webUrl) {
                    if (Platform.OS === 'web') {
                        window.open(
                            data?.stepResponseObject?.webUrl,
                            '_self',
                        )
                    } else {
                        setMfcFetchURL(data?.stepResponseObject?.webUrl)
                        setButtonLoader(false);
                    }
                }
            },
            (error) => {
                if (error?.data?.statusCode && handleApiErr(error)) {
                    dispatch(showErrorSheet({
                        isErrorSheetVisible: true,
                        errorMessage: error?.data?.message,
                        closeOnBackdropPress: true,
                        errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                        errorHeading: error?.data?.header,
                        showErrorHeading: true,
                        btnName: 'Ok, got it!',
                    }))
                }
            }
        );
    }

    const handleMFCModalCallback = async () => {
        setMfcFetchURL('');
        removeUrlWithoutReload("mfc_fetch_response")

        try {
             const applicationId = await getApplicationId(userDetails?.linkedApplications[0]?.applicationId);

        const res = await axios.post(endpoints.fetchSDKCallback + applicationId);

        if (res?.data?.status === 'SUCCESS') {

            const response = await axios.get(endpoints.checkEnabledRTA + applicationId);

            const isFromMFCentral = response?.data?.enabledRtaFlow === "MfCentralFlow"
            const mfcFetchStatus = response?.data?.mfcFetchStatus

            if (mfcFetchStatus === "IN_PROGRESS") {
                navigation.navigate(ROUTE.MFC_FETCH_EVALUTE_FUNDS)
            } else if (isFromMFCentral) {
                navigation.navigate(ROUTE.MFC_FETCH_ANCHOR)
            } else {
                navigation.navigate(
                    getNextRoute(
                        userDetails?.linkedApplications[0]?.currentStepId,
                        userDetails?.linkedApplications[0].applicationState,
                        userDetails?.linkedApplications[0].stepStatusMap
                    )
                );
            }
        } else {
        }
            
        } catch (error) {
            
        }

       
    }

    const getRtaDetailFromNetwork = async () => {
        setRtaDetailsFetching(true);
        const applicationId = userDetails?.linkedApplications[0]?.applicationId;
        await getRtaDetails(
            applicationId,
            (data: { flowType : any ,rtaDetails : DefaultRTA[]}) => {
                setDefaultRTAData(data?.rtaDetails.map((item: DefaultRTA) => ({...item, defaultRta: item.defaultRta === true})));
                setRtaDetailsFetching(false);
                setTitle(buildTitle(data?.rtaDetails));
                setFetchFlowType(data?.flowType)
            },
            (error) => {
                if (error?.data?.statusCode && handleApiErr(error)) {
                    dispatch(showErrorSheet({
                        isErrorSheetVisible: true,
                        errorMessage: error?.data?.message,
                        closeOnBackdropPress: true,
                        errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                        errorHeading: error?.data?.header,
                        showErrorHeading: true,
                        btnName: 'Ok, got it!',
                    }))
                }
                console.log('error', error);
                setRtaDetailsFetching(false);
            }
        );
        setRtaDetailsFetching(false);
    }

    useEffect(() => {
        if(userDetails) {
            if(userDetails?.linkedBorrowerAccounts[0]?.accountHolderPAN) {
                setPanNumber(userDetails?.linkedBorrowerAccounts[0]?.accountHolderPAN);
                setPanEditable(false);
            } else {
                setShowEditablePanFlow(true);
            }
            setMobileNumber(userDetails?.linkedBorrowerAccounts[0]?.accountHolderPhoneNumber?.slice(3));
            getRtaDetailFromNetwork();
        }
    }, [userDetails])

    const fetchShowLoadingLottie = async () => {
       
        fetch("https://cdn.lottielab.com/l/NbBPwb7esAxda0.json", {
            method: "GET",
        })
            .then((response) => response.json())
            .then((responseData) => {
                console.log(responseData);
                setShowLoadingLottie(responseData)
            })
            .catch((error) => {
                console.log(error);
            })
        
    }

    useEffect(() => {
        if (
          isEnhancementApplicationInProgress &&
          userDetails && showInfo &&
          defaultRTAData && defaultRTAData?.length > 0 &&
          userDetails?.linkedBorrowerAccounts[0]?.accountHolderPAN &&
          userDetails?.linkedBorrowerAccounts[0]?.accountHolderPhoneNumber
        ) {
          fetchShowLoadingLottie();
          fetchPortfolioInit();
          setHasFetchErrorOccured(false);
        }
      }, [
        isEnhancementApplicationInProgress,
        defaultRTAData
      ]);

    const retryFetchPortfolioInit = async () => {
        // const alternateRTA = defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)?.assetRepository === AssetRepository.CAMS
        //     ? AssetRepository.KARVY
        //     : AssetRepository.CAMS;
        // change the defaultRTA to the alternateRTA
        const updatedDefaultRTAData = defaultRTAData?.map((item: DefaultRTA) => ({
            ...item, 
            defaultRta: item?.assetRepository !== AssetRepository.MFCENTRAL
                && item?.defaultRta === false
                ? true : false
        }))
        defaultRTAData &&
        await setDefaultRTAData(updatedDefaultRTAData || []);
       
        await handleFetchPortfolioInit({
            payload: {
                applicationId: await getApplicationId(userDetails?.linkedApplications[0]?.applicationId),
                assetRepository: updatedDefaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)?.assetRepository || '',
                emailId: userDetails?.linkedBorrowerAccounts[0]?.accountHolderEmail || '',
                isRetryAllowed: false,
                phoneNumber: '+91' + mobileNumber,
                panNumber: panNumber || ''
            },
            successCallback: (data: FetchPortfolioInitResponse) => {
                setButtonLoader(false);
                setErrorModalData(null);
                if (data?.stepResponseObject?.error) {
                    setErrorModalData(data?.stepResponseObject?.error);
                } else {
                    setPortfolioInitData(data);
                }
            },
            errorCallback: (err: any) => {
                setErrorModalData(null);
                setPortfolioInitData(null);
                setButtonLoader(false);
            }
        });

        // setErrorModalData(null);
    }

    const fetchPortfolioInit = async() => {
        setButtonLoader(true);
        if(fetchFlowType === 'FENIX_SDK') {
           await getFetchSDKDataFromNetwork();
            //    setButtonLoader(false)
        } else {
        if(retryErrorCount >=3){
            setRetryErrorCount(0)
            trackEvent('MF_FETCH_PORTFOLIO_MAX_RETRY_LIMIT_REACHED', {source :rtaToRetry ? rtaToRetry : defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)?.assetRepository || ''})
            dispatch(
                showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: 'We are facing some issue with our technical systems, please try again later.',
                    closeOnBackdropPress: true,
                    errorHeading : "Service temporarily unavailable",
                    errorType: 'MAX_RETRY_LIMIT_REACHED',
                    routeName: route?.name,
                    ctaLabel: 'Okay'
                }),
            )
            return;
        }
        setShowSuccessLottie(true)
        setShowInfo(false)
        setTimeout(async () => {
        handleFetchPortfolioInit({
            payload: {
                applicationId: await getApplicationId(userDetails?.linkedApplications[0]?.applicationId),
                assetRepository: rtaToRetry ? rtaToRetry : defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)?.assetRepository || '',
                emailId: userDetails?.linkedBorrowerAccounts[0]?.accountHolderEmail || '',
                isRetryAllowed: true,
                phoneNumber: '+91' + mobileNumber,
                panNumber: panNumber || ''
            },
            successCallback: (data: FetchPortfolioInitResponse) => {
                setRetryErrorCount(0)
                setButtonLoader(false);
                setShowSuccessLottie(false);
                if (
                    userDetails?.linkedBorrowerAccounts[0]?.accountHolderPAN === null &&
                    editableErroCode(data?.stepResponseObject?.error?.errorCode || '')
                ) {
                    setPanEditable(true);
                }
                if (
                    data?.stepResponseObject?.error &&
                    data?.stepResponseObject?.error?.retryNextRTA
                ) {
                    setHasFetchErrorOccured(true);
                    if(retryingFetchPortfolio) {
                        setErrorModalData({
                            ...data?.stepResponseObject?.error,
                            retryType: FetchPortfolioModalType.IMMEDIATE
                        });
                    } else if (data?.stepResponseObject?.error?.retryType === FetchPortfolioModalType.IMMEDIATE) {
                        //Handle IMMEDIATE case
                        setErrorModalData(null);
                        retryFetchPortfolioInit();
                    } else {
                        //Show loader modal
                        setErrorModalData(data?.stepResponseObject?.error);
                        //Handle LOADER case
                        setTimeout(() => {
                            setErrorModalData(null);
                            retryFetchPortfolioInit();
                        }, data?.stepResponseObject?.error?.loaderTimingInMilliSec);
                    }
                } else if(data?.stepResponseObject?.error) {
                    setErrorModalData(data?.stepResponseObject?.error);
                } else {
                    setPortfolioInitData(data);
                }
            },
            errorCallback: (err: any) => {
                setRetryErrorCount(retryErrorCount + 1)
                if (err?.data?.statusCode && handleApiErr(err)) {
                    dispatch(showErrorSheet({
                        isErrorSheetVisible: true,
                        errorMessage: err?.data?.message,
                        closeOnBackdropPress: true,
                        errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                        errorHeading: err?.data?.header,
                        showErrorHeading: true,
                        btnName: 'Ok, got it!',
                    }))
                }
                setPortfolioInitData(null);
                setButtonLoader(false);
                setShowSuccessLottie(false)
                }
            })
        }, isEnhancementApplicationInProgress ? 2000 : 0)
        }
        
    };

    const renderNeedHelp = () => {
        return (
            <BottomSheetContainer
                isVisible={showNeedHelp}
                close={() => {
                    setShowNeedHelp(false);
                }}
                closeOnBackdropPress={true}
                borderTopRightRadius={10}
                borderTopLeftRadius={10}
            >
              {/* @ts-expect-error */}
              <ContactUs />
            </BottomSheetContainer>
        )
    }

    const actionMapping = (action: ACTION_TYPE) => {
        switch (action) {
            case ACTION_TYPE.EDIT_DETAILS:
                return () => {
                    setErrorModalData(null);
                };
            case ACTION_TYPE.CONTACT_US:
                return () => {
                    setErrorModalData(null);
                    setShowNeedHelp(true);
                };
            case ACTION_TYPE.NO_ACTION:
                return () => { };
            default:
                return () => { };
        }
    }

    const onVerifySuccess = (data: FetchPortfolioInitResponse) => {
        // setIsOtpModalVisible(false);
        setPortfolioInitData(null);
        const existingCurrentStep = userDetails?.linkedApplications[0]?.currentStepId;
        let updatedUserDetail = {
            ...userDetails,
            linkedApplications: [
                data.updatedApplicationObj
            ]
        }
        dispatch(updateUserDetails(updatedUserDetail));
        data?.stepResponseObject?.success &&
        dispatch(setAuthCAS(data?.stepResponseObject?.success));
        //Save the pan number to the user details slice to prevent api call for pan number
        panNumber && dispatch(updateUserDetailPan(panNumber));
        const nextStep = getNextRoute(
            updatedUserDetail?.linkedApplications[0]?.currentStepId, 
            updatedUserDetail?.linkedApplications[0]?.applicationState, 
            updatedUserDetail?.linkedApplications[0]?.stepStatusMap,
            undefined,
            undefined,
            {
                isFetchFirstTime: existingCurrentStep === CreditApplicationStepId.MF_FETCH_PORTFOLIO
            }
        )
        onSuccessFetch &&
        onSuccessFetch();

        rtaToRetry !== '' || rtaToRetry !== undefined && setTimeout(() => {
          if(defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)?.repositoryName === "MFCentral") {
            navigation.navigate(ROUTE.MFC_FETCH_EVALUTE_FUNDS);
          } else {
            if(Platform.OS === 'web') {
            navigation.navigate(nextStep as any);
            } else {
               navigation.reset({
                        index: 0,
                        routes: [{ name: 'HOME' }, { name : nextStep }],
                    })

            }
          }
        }, 250);

        setTimeout(() => {
            fetchUserProfile()
        }, 0)
    }


    const renderFooter = useMemo(() => {
        return (
            Platform.OS === 'web' || !isKeyboardOpen ? 
            <Footer
                defaultRTA={defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)}
                rtaDetailsFetching={rtaDetailsFetching}
                onEditRTA={() => setOpenRTAEdit(true)}
                onFetchPortfolio={() => {
                    fetchPortfolioInit()
                    setHasFetchErrorOccured(false)
                }}
                buttonLoader={buttonLoader}
                routeName={route.name}
                retryingFetchPortfolio={retryingFetchPortfolio}
                panNumber={panNumber}
                mobileNumber={mobileNumber}
                showEditablePanFlow={showEditablePanFlow}
                panEditable={panEditable}
                hideSubText={fetchFlowType === 'FENIX_SDK'}
                    fetchFlowType={fetchFlowType}
            /> : <></>
        )
    }, [
        defaultRTAData,
        rtaDetailsFetching,
        buttonLoader,
        route.name,
        retryingFetchPortfolio,
        panNumber,
        retryErrorCount,
        showEditablePanFlow,
        mobileNumber,
        panEditable,
        isKeyboardOpen,
        fetchFlowType
    ])



    return (
        <>
          {pageLoader ? <PageLoader /> :  <KeyboardDismissView>
                <>
                <ThemedView bg='bg-fill-primary' flex={isFullScreen ? 1 : undefined}>
                    {
                        !retryingFetchPortfolio &&
                        <Header
                            navigation={navigation}
                            routeName={route.name}
                            isEnhancementApplicationInProgress={isEnhancementApplicationInProgress}
                        />
                    }
                    {
                        rtaToRetry ?
                        <Body
                                    panNumber={panNumber}
                                    mobileNumber={mobileNumber}
                                    defaultRTA={defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)}
                                    onEditMobileNumber={() => {
                                        setOpenUpdateMobileNumber(true)
                                        trackEvent('EDIT_MOBILE_NUMBER_CLICKED')
                                    }}
                                    onEditRTA={() => {
                                        setOpenRTAEdit(true)
                                    }}
                        />
                        :
                        <EditPanFlow
                            panNumber={panNumber}
                            mobileNumber={mobileNumber}
                            setPanNumber={setPanNumber}
                            setMobileNumber={setMobileNumber}
                            panEditable={panEditable}
                            errorModalData={errorModalData}
                        />
                    }
                    {
                        retryingFetchPortfolio &&
                        <ThemedView
                            style={{
                                height: 100
                            }}
                        />
                    }
                    {}
                </ThemedView>
                    {(Platform.OS !== 'web' || !openUpdateMobileNumber) && renderFooter}
                <EditMobileNumberModal
                    isVisible={openUpdateMobileNumber}
                    onClose={() => {
                        setOpenUpdateMobileNumber(false);
                        setErrorModalData(null);
                        trackEvent('MF_FETCH_PORTFOLIO_MOBILE_NUMBER_UPDATE_CLOSED')
                    }}
                    mobileNumberTemp={mobileNumber}
                    onUpdate={(mobileNumber: string) => {
                        setMobileNumber(mobileNumber);
                        setOpenUpdateMobileNumber(false);
                    }}
                    buttonLoader={buttonLoader}
                    routeName={route.name}
                />
                    {
                    defaultRTAData && defaultRTAData?.length > 0 && (
                        <EditRtaModal
                            title={title}
                            isVisible={openRTAEdit}
                            onClose={() => {
                                setOpenRTAEdit(false);
                                setErrorModalData(null);
                            }}
                            defaultRTAData={defaultRTAData || []}
                            defaultSelectedRTA={defaultRTAData && defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true) || null}
                            onConfirm={(rtaData: DefaultRTA[]) => {
                                setOpenRTAEdit(false);
                                setDefaultRTAData(rtaData);
                                trackEvent('GET_PORTFOLIO_SOURCE_UPDATED', {
                                    source: 'MF_FETCH_PORTFOLIO',
                                    rtaName: defaultRTAData?.find((rta: DefaultRTA) => rta.defaultRta === true)?.repositoryName,
                                    rtaCount: defaultRTAData?.length,
                                })
                            }}
                            routeName={route.name}
                        />
                    )
                }
                <VerifyOtpModal
                    isVisible={portfolioInitData !== null}
                    onClose={() => {
                        setPortfolioInitData(null);
                    }}
                    portfolioInitData={portfolioInitData}
                    userDetails={userDetails}
                    defaultRTA={rtaToRetry ?
                        defaultRTAData?.find((item: DefaultRTA) => item.assetRepository === rtaToRetry) 
                        : defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)}
                    mobileNumber={mobileNumber}
                    onFetchPortfolio={fetchPortfolioInit}
                    setErrorModalData={setErrorModalData}
                    setPortfolioInitData={setPortfolioInitData}
                    onRetryFetch={retryFetchPortfolioInit}
                    setHasFetchErrorOccured={setHasFetchErrorOccured}
                    hasFetchErrorOccured={hasFetchErrorOccured}
                    onVerifySuccess={(data: FetchPortfolioInitResponse) => {
                        onVerifySuccess(data)
                    }}
                    retryingFetchPortfolio={retryingFetchPortfolio}
                    
                />
                {
                    errorModalData && (
                        <FetchPortfolioModals
                            visible={errorModalData !== null}
                            type={errorModalData?.retryType}
                            loadingTime={errorModalData?.loaderTimingInMilliSec}
                            title={errorModalData?.header}
                            subtitle={errorModalData?.message}
                            primaryCtaTitle={errorModalData?.primaryCtaText}
                            primaryCtaAction={actionMapping(errorModalData?.primaryCtaAction || ACTION_TYPE.NO_ACTION)}
                            tertiaryCTATitle={errorModalData?.tertiaryCtaText}
                            tertiaryCTAAction={actionMapping(errorModalData?.tertiaryCtaAction || ACTION_TYPE.NO_ACTION)}
                            loaderText={`Fetching your portfolio from ${defaultRTAData?.find((item: DefaultRTA) => item.defaultRta === true)?.repositoryName === AssetRepository.CAMS ? 'KARVY' : 'CAMS'}`}
                            onModalClose={() => {
                                setErrorModalData(null);
                            }}
                        />
                    )
                }
                {renderNeedHelp()}
                    <BottomSheetContainer
                        showCloseIcon={true}
                        isVisible={mfcFetchURL !== ''}
                        close={() => {
                            setMfcFetchURL('')
                        }}
                        onClose={() => {
                            setMfcFetchURL('')

                        }}
                        containerStyle={{ borderTopRightRadius: 16, borderTopLeftRadius: 16 }}
                        closeOnBackdropPress={false}
                        borderTopRightRadius={10}
                        borderTopLeftRadius={10}
                        keyboardHandlerEnabled={false}
                    >
                        <MFCFetchModal url={mfcFetchURL} onFetchComplete={handleMFCModalCallback} />
                    </BottomSheetContainer>
                {/* {showSuccessLottie && showLoadingLottie  && isEnhancementApplicationInProgress && !buttonFetching && renderFetchLoader()} */}
                </>
      
            </KeyboardDismissView>
          }
        </>
    );
};

export default FetchPortfolio;